import { Link, Navigate, Route, Routes } from "react-router-dom"
import { type AuthContext, useAdminAuth } from "./components/AdminAuthGate.tsx"
import { type DockItem, FloatingDock } from "./components/FloatingDock.tsx"
import { KnowledgeLogo } from "./components/KnowledgeLogo.tsx"
import { BlocksPage } from "./pages/BlocksPage.tsx"
import { PacksPage } from "./pages/PacksPage.tsx"
import { ServerDetailPage } from "./pages/ServerDetailPage.tsx"
import { ServiceDetailPage } from "./pages/ServiceDetailPage.tsx"
import { ServicesPage } from "./pages/ServicesPage.tsx"

export function App() {
	const { context, login, checking } = useAdminAuth()
	if (checking) {
		return (
			<div className="auth-wrap">
				<div className="auth-card" style={{ textAlign: "center" }}>
					<div className="field-hint">세션 확인 중...</div>
				</div>
			</div>
		)
	}
	if (!context) return login

	const navItems: ReadonlyArray<DockItem> = [
		{
			to: "/services",
			label: "서비스",
			icon: (
				<svg
					className="icon"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden
				>
					<title>서비스</title>
					<rect x="3" y="3" width="7" height="7" rx="1" />
					<rect x="14" y="3" width="7" height="7" rx="1" />
					<rect x="3" y="14" width="7" height="7" rx="1" />
					<rect x="14" y="14" width="7" height="7" rx="1" />
				</svg>
			)
		},
		{
			to: "/packs",
			label: "팩",
			icon: (
				<svg
					className="icon"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden
				>
					<title>팩</title>
					<path d="M12 2 2 7l10 5 10-5-10-5z" />
					<path d="M2 17l10 5 10-5" />
					<path d="M2 12l10 5 10-5" />
				</svg>
			)
		},
		{
			to: "/blocks",
			label: "블럭",
			icon: (
				<svg
					className="icon"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden
				>
					<title>블럭</title>
					<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
					<path d="M3.27 6.96 12 12.01l8.73-5.05" />
					<path d="M12 22.08V12" />
				</svg>
			)
		},
		{
			to: "/graph",
			label: "온톨로지 워크벤치",
			icon: (
				<svg
					className="icon"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden
				>
					<title>온톨로지 워크벤치</title>
					<circle cx="6" cy="7" r="2" />
					<circle cx="18" cy="7" r="2" />
					<circle cx="6" cy="17" r="2" />
					<circle cx="18" cy="17" r="2" />
					<circle cx="12" cy="12" r="2.5" />
					<path d="M7.5 8 10.5 11" />
					<path d="M16.5 8 13.5 11" />
					<path d="M7.5 16 10.5 13" />
					<path d="M16.5 16 13.5 13" />
				</svg>
			)
		},
		{
			href: "/docs/",
			external: true,
			label: "문서",
			icon: (
				<svg
					className="icon"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden
				>
					<title>문서</title>
					<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
					<path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
				</svg>
			)
		}
	]

	return (
		<div className="app">
			<header className="app-header">
				<Link
					to="/services"
					className="app-header__logo-link"
					title="서비스 목록으로"
					aria-label="서비스 목록으로"
				>
					<KnowledgeLogo height={28} />
				</Link>
				<UserBadge user={context.user} onLogout={() => void context.logout()} />
			</header>
			<main>
				<Routes>
					<Route path="/" element={<Navigate to="/services" replace />} />
					<Route path="/services" element={<ServicesPage auth={context} />} />
					<Route path="/services/:slug" element={<ServiceDetailPage auth={context} />} />
					<Route path="/services/:slug/servers/:name" element={<ServerDetailPage auth={context} />} />
					<Route path="/packs" element={<PacksPage auth={context} />} />
					<Route path="/blocks" element={<BlocksPage auth={context} mode="catalog" />} />
					<Route path="/graph" element={<BlocksPage auth={context} mode="graph" />} />
				</Routes>
			</main>
			<FloatingDock items={navItems} />
		</div>
	)
}

/**
 * 상단 헤더 우측 — 로그인 계정 정보 + 로그아웃.
 * 이니셜 avatar · displayName · email(선택) · 로그아웃 아이콘 버튼.
 */
function UserBadge({
	user,
	onLogout
}: {
	readonly user: AuthContext["user"]
	readonly onLogout: () => void
}) {
	const name = user.displayName
	const initial = name.trim().charAt(0).toUpperCase() || "?"
	return (
		<div className="app-header__user" title={user.email ?? name}>
			<span className="app-header__user-avatar" aria-hidden>
				{initial}
			</span>
			<span className="app-header__user-name">{name}</span>
			{user.email && user.email !== name && <span className="app-header__user-email">· {user.email}</span>}
			<button
				type="button"
				className="app-header__user-logout"
				onClick={onLogout}
				aria-label="로그아웃"
				title="로그아웃"
			>
				<svg
					width="14"
					height="14"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden
				>
					<title>로그아웃</title>
					<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
					<polyline points="16 17 21 12 16 7" />
					<line x1="21" y1="12" x2="9" y2="12" />
				</svg>
			</button>
		</div>
	)
}
